import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  concat,
  concatMap,
  defer,
  EMPTY,
  forkJoin,
  map,
  MonoTypeOperatorFunction,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { Option } from '@sapphire/result';

import { APP_ROUTES } from '@ps/core-config';
import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { SupplierService, SurveyService } from '@ps/survey-data-access';
import { SURVEY_STATUS, SurveyStatus } from '@ps/survey-kernel';
import {
  hasSharedLaunchSettings,
  launchPricingSummary,
  launchStatusErrorMessage,
  LaunchBillingContext,
  MC_TRADITIONAL_MESSAGES,
  sharedLaunchSettings,
  Survey,
  SURVEY_CLASSIFICATION_KIND,
  SurveyLaunchForm,
} from '@ps/survey-utils';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';

import { FlexibilityPricesService } from '../../services/flexibility-prices/flexibility-prices.service';
import {
  selectCoreChildren,
  selectDraftChildSurveyIds,
  selectKind,
  selectSurveyId,
} from '../core/survey-creation-core.selectors';
import { SurveyCreationCoreActions } from '../core/survey-creation-core.actions';
import { SurveyChildKey } from '../survey-creation-shared-state.model';
import { SurveyChildRegistryState } from '../survey-creation-state.model';
import { selectLaunchForm } from './survey-creation-launch.selectors';
import {
  LaunchPricingConfirmation,
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from './survey-creation-launch.actions';

// The save half of a launch lives in SurveyCreationSaveEffects (it reuses the
// save pipeline); this class owns what happens once the survey is persisted.
@Injectable()
export class SurveyCreationLaunchSubmitEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _router = inject(Router);
  private readonly _toast = inject(ToastService);

  private readonly _surveyService = inject(SurveyService);
  private readonly _supplierService = inject(SupplierService);
  private readonly _flexibilityPricesService = inject(FlexibilityPricesService);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);

  public readonly loadLaunchPricing$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.loadLaunchPricing),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      switchMap(([, surveyId]) =>
        Option.from(surveyId).match({
          some: (id) =>
            forkJoin([
              this._flexibilityPricesService.get(id),
              this._supplierService.allocations(id, { scope: 'launch' }),
            ]).pipe(
              map(([prices, suppliers]) =>
                SurveyCreationLaunchApiActions.launchPricingLoaded({
                  pricing: launchPricingSummary(prices, suppliers),
                }),
              ),
              this.psCatchError(
                (error) =>
                  of(
                    SurveyCreationLaunchApiActions.launchPricingFailure({
                      error: error.message,
                    }),
                  ),
                { notify: true },
              ),
            ),
          none: () =>
            of(
              SurveyCreationLaunchApiActions.launchPricingFailure({
                error: 'No survey to price',
              }),
            ),
        }),
      ),
    ),
  );

  public readonly changeLaunchStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchApiActions.changeLaunchStatus),
      switchMap(({ surveyId, status, pricingConfirmations }) =>
        this._logPricingConfirmations(pricingConfirmations).pipe(
          concatMap(() => this._updateStatusAndAfter(surveyId, status)),
          this._catchLaunchFailure(),
        ),
      ),
    ),
  );

  // "Launch all Surveys": a sibling locale not currently being edited — no
  // pricing confirmation was shown for it (the bulk flow confirms once,
  // up front, for every locale together), so there is nothing to log.
  public readonly launchSiblingSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.launchSiblingSurvey),
      concatLatestFrom(() => this._store.select(selectCoreChildren)),
      // Only the status changes here: the batch toasts once and leaves the
      // page when every locale is done (finishLaunchAll$).
      concatMap(([{ surveyId, status }, children]) =>
        this._surveyService.updateSurveyStatus(surveyId, status, 'page').pipe(
          map(() =>
            SurveyCreationLaunchApiActions.launchSurveySuccess({
              surveyId,
              status,
            }),
          ),
          this._catchLaunchFailure(registeredSurvey(children, surveyId)),
        ),
      ),
    ),
  );

  // "Launch all Surveys" launched at least one locale: one toast for the
  // batch, counting the locales left in Draft, then off to the Dashboard.
  public readonly finishLaunchAll$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.finishLaunchAll),
      concatLatestFrom(() => this._store.select(selectDraftChildSurveyIds)),
      tap(([, draftSurveyIds]) =>
        this._toast.success(launchAllMessage(draftSurveyIds.length)),
      ),
      switchMap(() =>
        concat(
          of(SurveyCreationCoreActions.reset()),
          defer(() => this._navigateTo(APP_ROUTES.DASHBOARD)),
        ),
      ),
    ),
  );

  // "Launch all Surveys": a single combined PATCH of the active locale's own
  // communication/billing/URL launch settings, same endpoint and field set
  // as legacy's pre-launch save — not the full survey save pipeline, whose
  // validation gate can silently drop an invalid save with no terminal action.
  public readonly patchLaunchSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.patchLaunchSettings),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectLaunchForm),
        this._store.select(selectCoreChildren),
      ]),
      switchMap(([, surveyId, launchForm, children]) =>
        Option.from(surveyId).match({
          some: (id) =>
            this._surveyService
              .patchLaunchSettings(id, {
                subscriberEmails:
                  launchForm.value.communication.subscriberEmails,
                pmEmailsInvoice:
                  launchForm.value.billingDetails.pmEmailsInvoice,
                billingId: launchForm.value.billingDetails.billingId,
                buyerProjectManagerId:
                  launchForm.value.communication.buyerProjectManager?.id ??
                  null,
                liveUrl: launchForm.value.surveyUrls.liveUrl,
                testUrl: launchForm.value.surveyUrls.testUrl,
              })
              .pipe(
                concatMap(() =>
                  this._patchSiblingsWithSharedSettings(
                    id,
                    launchForm.value,
                    children,
                  ),
                ),
                map(() =>
                  SurveyCreationLaunchApiActions.patchLaunchSettingsSuccess(),
                ),
                this.psCatchError(
                  (error) =>
                    of(
                      SurveyCreationLaunchApiActions.patchLaunchSettingsFailure(
                        { error: error.message },
                      ),
                    ),
                  { notify: true },
                ),
              ),
          none: () =>
            of(
              SurveyCreationLaunchApiActions.patchLaunchSettingsFailure({
                error: 'No survey to save',
              }),
            ),
        }),
      ),
    ),
  );

  // "Use the same ... for all locales": every sibling locale gets the fields
  // whose section the buyer ticked, so they carry the Live URL (and billing /
  // communication) the batch is about to launch them with. The active locale
  // is skipped — the full patch above already wrote all of its fields — and a
  // section left unticked contributes no op, so a sibling keeps its own.
  private _patchSiblingsWithSharedSettings(
    activeSurveyId: number,
    launchForm: SurveyLaunchForm,
    children: Record<SurveyChildKey, SurveyChildRegistryState>,
  ): Observable<unknown> {
    const shared = sharedLaunchSettings(launchForm);

    if (!hasSharedLaunchSettings(shared)) {
      return of(undefined);
    }

    const siblingIds = Object.values(children).flatMap(({ survey }) =>
      survey && survey.id !== activeSurveyId ? [survey.id] : [],
    );

    if (!siblingIds.length) {
      return of(undefined);
    }

    return forkJoin(
      siblingIds.map((siblingId) =>
        this._surveyService.patchSharedLaunchSettings(siblingId, shared),
      ),
    );
  }

  private _updateStatusAndAfter(
    surveyId: number,
    status: SurveyStatus,
  ): Observable<Action> {
    return this._surveyService
      .updateSurveyStatus(surveyId, status, 'page')
      .pipe(
        tap(() => this._toast.success(launchedMessage(status))),
        switchMap(() => this._afterLaunch(surveyId, status)),
      );
  }

  // Swaps the raw backend message for the billing-aware one, then turns
  // whatever failed upstream of it (logging the pricing confirmation,
  // changing the status) into one shared failure action.
  // A "Launch all Surveys" locale is named in its message, so the buyer knows
  // which one failed.
  private _catchLaunchFailure(
    locale: Option<Survey> = Option.none,
  ): MonoTypeOperatorFunction<Action> {
    return (source$) =>
      source$.pipe(
        catchError((error: unknown) => {
          const message = launchStatusErrorMessage(
            error,
            this._billingContext(),
          );
          return throwError(
            () =>
              new Error(
                locale.mapOr(message, (survey) =>
                  localeLaunchErrorMessage(message, survey),
                ),
              ),
          );
        }),
        this.psCatchError(
          (error) =>
            of(
              SurveyCreationLaunchApiActions.launchSurveyFailure({
                error: error.message,
              }),
            ),
          { notify: true },
        ),
      );
  }

  // Emits the success action, then resets+navigates once there is nothing
  // left to launch (see _whenAllLaunched) — shared by the single-survey path
  // and every "Launch all Surveys" sibling, so only the last one to finish
  // ever triggers it.
  private _afterLaunch(
    surveyId: number,
    status: SurveyStatus,
  ): Observable<Action> {
    return concat(
      of(
        SurveyCreationLaunchApiActions.launchSurveySuccess({
          surveyId,
          status,
        }),
      ),
      this._whenAllLaunched().pipe(
        switchMap(() =>
          concat(
            of(SurveyCreationCoreActions.reset()),
            defer(() => this._navigateTo(APP_ROUTES.DASHBOARD)),
          ),
        ),
      ),
    );
  }

  // Every confirmation with a message must be logged before the caller moves
  // on; forkJoin([]) would complete without emitting, so an empty batch
  // resolves straight away.
  private _logPricingConfirmations(
    confirmations: readonly LaunchPricingConfirmation[],
  ): Observable<void> {
    const logs = confirmations
      .filter(({ message }) => !!message)
      .map(({ surveyId, message }) =>
        this._surveyService.logPricingConfirmation(surveyId, message),
      );

    return logs.length
      ? forkJoin(logs).pipe(map(() => undefined))
      : of(undefined);
  }

  // Emits once reset+navigate should run: every kind but MC Traditional
  // always qualifies; MC Traditional only once no sibling locale is left
  // Draft (otherwise "Launch all"/the single button still has work to do).
  private _whenAllLaunched(): Observable<void> {
    return combineLatest([
      this._store.select(selectKind),
      this._store.select(selectDraftChildSurveyIds),
    ]).pipe(
      take(1),
      switchMap(([kind, draftSurveyIds]) =>
        kind?.kind === SURVEY_CLASSIFICATION_KIND.MC && draftSurveyIds.length
          ? EMPTY
          : of(undefined),
      ),
    );
  }

  private _billingContext(): LaunchBillingContext {
    return {
      rulesEnabled:
        !!this._userSettingsSelectors.buyerSettings().enableBillingNumberRules,
      example: Option.from(this._userSettingsSelectors.billingRuleRecord()).map(
        (record) => record.example,
      ),
    };
  }

  // Navigates as a side effect and contributes no action to the stream.
  private _navigateTo(route: string): Observable<never> {
    this._router.navigate([route]);
    return EMPTY;
  }
}

function registeredSurvey(
  children: Record<SurveyChildKey, SurveyChildRegistryState>,
  surveyId: number,
): Option<Survey> {
  return Option.from(
    Object.values(children).find(({ survey }) => survey?.id === surveyId)
      ?.survey,
  );
}

function localeLaunchErrorMessage(message: string, survey: Survey): string {
  return `Survey ${survey.countryName}-${survey.languageName} - ${message}`;
}

function launchAllMessage(stillDraftCount: number): string {
  return stillDraftCount
    ? MC_TRADITIONAL_MESSAGES.PARTIALLY_LAUNCHED.replace(
        '<COUNT>',
        String(stillDraftCount),
      )
    : MC_TRADITIONAL_MESSAGES.ALL_LAUNCHED;
}

function launchedMessage(status: SurveyStatus): string {
  return status === SURVEY_STATUS.SCHEDULED
    ? 'Survey will be launched at the scheduled time launch'
    : 'Survey successfully launched';
}
